<script setup lang="ts">
import { computed } from 'vue'
import { RouterLink, useRoute, useRouter } from 'vue-router'
import Button from 'primevue/button'

import { useAuthStore } from '@/stores/auth'
import { useProjectsStore } from '@/stores/projects'

interface Props {
  collapsed?: boolean
}
defineProps<Props>()

const route = useRoute()
const router = useRouter()
const authStore = useAuthStore()
const projects = useProjectsStore()

const projectId = computed(() => {
  const p = route.params.id
  if (typeof p === 'string' && !isNaN(parseInt(p, 10))) {
    return parseInt(p, 10)
  }
  return null
})

const workbooksLink = computed(() => {
  if (!projectId.value) return { name: 'customer-login' }
  return {
    name: 'customer-workbooks',
    params: { id: String(projectId.value) },
  }
})

const filesLink = computed(() => {
  if (!projectId.value) return { name: 'customer-login' }
  return {
    name: 'customer-files',
    params: { id: String(projectId.value) },
  }
})

const settingsLink = computed(() => {
  if (!projectId.value) return { name: 'customer-login' }
  return {
    name: 'customer-settings',
    params: { id: String(projectId.value) },
  }
})

async function handleLogout(): Promise<void> {
  await authStore.logout()
  void router.push({ name: 'customer-login' })
}
</script>

<template>
  <div class="customer-sidebar" data-testid="customer-sidebar-content">
    <nav class="customer-sidebar__nav" aria-label="Customer navigation">
      <RouterLink
        v-tooltip.right="{ value: collapsed ? 'Source Files' : undefined, showDelay: 300 }"
        :to="filesLink"
        class="customer-sidebar__link"
        :class="{
          'customer-sidebar__link--active':
            route.name === 'customer-files' || route.name === 'customer-file',
        }"
        :aria-label="collapsed ? 'Source Files' : undefined"
        data-testid="customer-sidebar-files"
      >
        <i class="pi pi-file-pdf" aria-hidden="true" />
        <span v-if="!collapsed" class="customer-sidebar__label">Source Files</span>
      </RouterLink>
      <RouterLink
        v-if="projects.featuresFor(projectId ?? 0).supports_workbook"
        v-tooltip.right="{ value: collapsed ? 'Workbooks' : undefined, showDelay: 300 }"
        :to="workbooksLink"
        class="customer-sidebar__link"
        :class="{ 'customer-sidebar__link--active': route.name === 'customer-workbooks' }"
        :aria-label="collapsed ? 'Workbooks' : undefined"
        data-testid="customer-sidebar-workbooks"
      >
        <i class="pi pi-table" aria-hidden="true" />
        <span v-if="!collapsed" class="customer-sidebar__label">Workbooks</span>
      </RouterLink>
    </nav>

    <div class="customer-sidebar__footer">
      <RouterLink
        v-tooltip.right="{ value: collapsed ? 'Settings' : undefined, showDelay: 300 }"
        :to="settingsLink"
        class="customer-sidebar__link customer-sidebar__settings"
        :class="{
          'customer-sidebar__link--active': route.name === 'customer-settings',
          'customer-sidebar__settings--collapsed': collapsed,
        }"
        :aria-label="collapsed ? 'Settings' : undefined"
        data-testid="customer-sidebar-settings"
      >
        <i class="pi pi-cog" aria-hidden="true" />
        <span v-if="!collapsed" class="customer-sidebar__label">Settings</span>
      </RouterLink>

      <div class="customer-sidebar__account">
        <div v-if="!collapsed" class="customer-sidebar__user">
          <span class="customer-sidebar__email" :title="authStore.user?.email || ''">
            {{ authStore.user?.email }}
          </span>
        </div>
        <Button
          v-tooltip.right="collapsed ? 'Sign out' : undefined"
          icon="pi pi-sign-out"
          severity="secondary"
          text
          rounded
          size="small"
          aria-label="Sign out"
          data-testid="customer-sidebar-logout"
          @click="handleLogout"
        />
      </div>
    </div>
  </div>
</template>

<style scoped>
.customer-sidebar {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  flex: 1;
}

.customer-sidebar__nav {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}

.customer-sidebar__link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
  font-weight: 500;
  text-decoration: none;
  transition:
    color var(--duration-fast),
    background var(--duration-fast);
}

.customer-sidebar__link:hover {
  color: var(--text-primary);
  background: var(--bg-card-hover);
}

.customer-sidebar__link--active {
  color: var(--green-primary);
  background: var(--bg-elevated);
}

.customer-sidebar__footer {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  padding-top: var(--space-3);
  border-top: var(--space-px) solid var(--border);
  margin-top: auto;
}

.customer-sidebar__settings {
  width: 100%;
}

.customer-sidebar__settings--collapsed {
  justify-content: center;
  padding-inline: 0;
}

.customer-sidebar__account {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-2);
}

.customer-sidebar__user {
  min-width: 0;
  flex: 1;
}

.customer-sidebar__email {
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-caption);
  color: var(--text-secondary);
}
</style>
